// components/LevelBar.js
// BuzzInApp — Barra de nivel del usuario
//
// Props:
//   totalClicks   number
//   compact       boolean — versión compacta para HomeScreen (sin detalles)

import React from "react";
import { getUserLevel, USER_LEVELS } from "../config/security";
import useTranslation from "../i18n/useTranslation";
import "./LevelBar.css";

import level1Img from "../img/level-1-buzzer.png";
import level2Img from "../img/level-2-hot.png";
import level3Img from "../img/level-3-diamond.png";
import level4Img from "../img/level-4-legend.png";

const LEVEL_IMAGES = { 1: level1Img, 2: level2Img, 3: level3Img, 4: level4Img };

const LevelBar = ({ totalClicks = 0, compact = false }) => {
  const { t } = useTranslation();

  const currentLevel = getUserLevel(totalClicks);
  const nextLevel    = USER_LEVELS.find((l) => l.level === currentLevel.level + 1);

  // Progreso dentro del nivel actual
  const progressPct = nextLevel
    ? Math.min(
        100,
        Math.round(
          ((totalClicks - currentLevel.minClicks) /
            (nextLevel.minClicks - currentLevel.minClicks)) *
            100
        )
      )
    : 100; // nivel máximo

  const clicksToNext = nextLevel
    ? nextLevel.minClicks - totalClicks
    : 0;

  if (compact) {
    return (
      <div className="levelbar levelbar--compact" aria-label={`Nivel ${currentLevel.name}`}>
        {LEVEL_IMAGES[currentLevel.level]
          ? <img src={LEVEL_IMAGES[currentLevel.level]} alt={currentLevel.name} className="levelbar__compact-img" />
          : <span className="levelbar__compact-emoji">{currentLevel.emoji}</span>
        }
        <div className="levelbar__compact-bar">
          <div
            className="levelbar__fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="levelbar__compact-label">{currentLevel.name}</span>
      </div>
    );
  }

  return (
    <div className="levelbar" aria-label={`Nivel actual: ${currentLevel.name}`}>
      {/* Nivel actual */}
      <div className="levelbar__header">
        <div className="levelbar__current">
          {LEVEL_IMAGES[currentLevel.level]
            ? <img src={LEVEL_IMAGES[currentLevel.level]} alt={currentLevel.name} className="levelbar__level-img" />
            : <span className="levelbar__emoji">{currentLevel.emoji}</span>
          }
          <div>
            <div className="levelbar__level-name">{currentLevel.name}</div>
            <div className="levelbar__clicks-total">
              {t("levels.totalClicks", { n: totalClicks })}
            </div>
          </div>
        </div>

        {nextLevel && (
          <div className="levelbar__next">
            {LEVEL_IMAGES[nextLevel.level]
              ? <img src={LEVEL_IMAGES[nextLevel.level]} alt={nextLevel.name} className="levelbar__next-img" />
              : <span className="levelbar__next-emoji">{nextLevel.emoji}</span>
            }
            <span className="levelbar__next-name">{nextLevel.name}</span>
          </div>
        )}
      </div>

      {/* Barra de progreso */}
      <div
        className="levelbar__track"
        role="progressbar"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="levelbar__fill"
          style={{ width: `${progressPct}%` }}
        />
        {/* Marcadores de nivel */}
        {USER_LEVELS.map((l, i) => {
          if (i === 0) return null;
          const pct = ((l.minClicks - currentLevel.minClicks) /
            ((nextLevel?.minClicks ?? currentLevel.minClicks + 1) - currentLevel.minClicks)) * 100;
          if (pct <= 0 || pct >= 100) return null;
          return (
            <span
              key={l.level}
              className="levelbar__marker"
              style={{ left: `${pct}%` }}
              aria-hidden="true"
            />
          );
        })}
      </div>

      {/* Texto de progreso */}
      {nextLevel ? (
        <div className="levelbar__progress-text">
          {t("levels.clicksToNext", { n: clicksToNext, level: nextLevel.name })}
        </div>
      ) : (
        <div className="levelbar__progress-text levelbar__progress-text--max">
          {t("levels.atMaxLevel")}
        </div>
      )}
    </div>
  );
};

export default LevelBar;